/**
 * `/admin/compliance-records/:recordId` — admin verification workflow
 * screen (M13, PRD 06 §Verification workflow, US-ADM-03 / US-ADM-04 /
 * US-ADM-05).
 *
 * Single-page verify flow: on mount fire
 * `GET /compliance-records/:recordId/detail` which returns the record +
 * slim person summary + full item type row + an inline-preview URL when
 * evidence is attached. Render the identity + evidence preview
 * side-by-side so the admin can confirm the name on the certificate
 * matches `people.full_name` without flipping tabs, then capture the
 * verification notes and submit `POST /compliance-records/:recordId/verify`
 * or, for WWCC non-valid outcomes that need fresh volunteer input, the
 * sibling `POST /compliance-records/:recordId/request-reverification`.
 *
 * Scope for this file:
 *   - Non-WWCC verify (Foundation certificate, typed-name
 *     acknowledgement). Outcome is pinned to `valid`; the form submits
 *     straight through the verify endpoint.
 *   - **WWCC verify** (new): the `WwccHelpPanel` renders alongside the
 *     form when `item_type.short_code === 'WWCC'`. It carries the full
 *     help copy from PRD 06 §WWCC verification — admin help panel
 *     (numbered steps, unusual-outcome branches, copy-on-click tiles
 *     for clearance number / name / DOB, an **Open portal** button
 *     landing on the OCG employer-verification portal, a "Last
 *     reviewed" date stamp). The verify form grows an outcome radio
 *     (`Valid` / `Barred` / `Interim Bar` / `Not Found`) and the
 *     "Prohibition notice check completed" checkbox per the day-one
 *     rollout fold-in. Branch rules:
 *       - `Valid` → verify endpoint with `outcome='valid'`; record →
 *         `verified`; person stays active.
 *       - `Barred` / `Interim Bar` → verify endpoint with outcome
 *         pass-through; the server stands the person down (the record
 *         is `verified` in the sense that the check was completed,
 *         even though the person isn't cleared to volunteer).
 *       - `Not Found` → **request-reverification** endpoint; record
 *         stays in `submitted`; the admin captures a free-form note
 *         for the volunteer, and the reason (`wwcc_not_found`) is
 *         stamped onto the record. A separate branch lets the admin
 *         request re-verification specifically because of a name or
 *         DOB mismatch — same endpoint, different reason code.
 *
 *   - **Upload on behalf** (PRD 06 §Edge cases) — the volunteer
 *     forwarded their certificate to admin out-of-band, or the
 *     volunteer can't / won't use `/me`. The `UploadOnBehalfPanel`
 *     renders above the verify form whenever the record is verifiable
 *     and captures a required note explaining why admin is uploading.
 *     It runs the three-step chain: `POST /uploads/request-url` →
 *     direct PUT → `POST /uploads/confirm` → `POST
 *     /compliance-records/:recordId/attach-evidence`. On success the
 *     record transitions to `submitted` and the inline preview
 *     refreshes so the admin can verify on the same page.
 *
 * What's deliberately out:
 *   - The `.mdx`-backed help copy lookup — the WWCC panel text lives
 *     in this file today so the slice doesn't grow a parallel
 *     content-loader pipeline. Moving the copy to `packages/web/admin/
 *     help/wwcc.mdx` is a pure refactor when that harness lands.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type {
  ComplianceRecordDetailResponse,
  ComplianceRecordStatus,
  PersonStatus,
  RequestReverificationReason,
  VerifyComplianceRecordResponse,
} from '@isms/shared';

export interface ComplianceRecordDetailPageProps {
  client: ApiClient;
  /** Where to send the admin on 401. Defaults to `/admin/sign-in`. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'not_found' }
  | { kind: 'ready'; data: ComplianceRecordDetailResponse };

/** Single submit state shared by both verify and request-reverification.
 *  The `success.kind` narrows how the success panel reads; UI code never
 *  has to branch on which endpoint produced the result. */
type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success_verify'; result: VerifyComplianceRecordResponse }
  | { kind: 'success_reverify_requested' }
  | { kind: 'error'; code: string; message: string; apiError: ApiError | null };

type WwccOutcome = 'valid' | 'barred' | 'interim_bar' | 'not_found';

const WWCC_OUTCOME_OPTIONS: readonly { value: WwccOutcome; label: string; hint: string }[] = [
  {
    value: 'valid',
    label: 'Valid',
    hint: 'OCG portal returned Cleared — proceed to verify.',
  },
  {
    value: 'barred',
    label: 'Barred',
    hint: 'OCG portal returned Barred — flag as blocked, do not verify.',
  },
  {
    value: 'interim_bar',
    label: 'Interim Bar',
    hint: 'OCG portal returned Interim Bar — flag as blocked, do not verify.',
  },
  {
    value: 'not_found',
    label: 'Not Found',
    hint: 'Portal could not find the number — request a fresh upload from the volunteer.',
  },
] as const;

const REVERIFY_REASON_OPTIONS: readonly {
  value: RequestReverificationReason;
  label: string;
}[] = [
  { value: 'wwcc_not_found', label: 'WWCC not found in the OCG portal' },
  { value: 'wwcc_name_mismatch', label: 'Name does not match the portal result' },
  { value: 'wwcc_dob_mismatch', label: 'Date of birth does not match the portal result' },
  { value: 'other', label: 'Other (explain in the note below)' },
] as const;

const RECORD_STATUS_LABELS: Record<ComplianceRecordStatus, string> = {
  pending: 'Pending — volunteer action required',
  submitted: 'Awaiting verification',
  verified: 'Verified',
  expiring_soon: 'Expiring soon',
  expired: 'Expired',
  superseded: 'Superseded',
  archived: 'Archived',
};

const VERIFIABLE_STATUSES: readonly ComplianceRecordStatus[] = ['pending', 'submitted'] as const;

/** Records eligible for the supersede action. Matches
 *  `SUPERSEDABLE_STATUSES` on the server — the API rejects supersede
 *  on any other status with `invalid_status_transition`. */
const SUPERSEDABLE_STATUSES: readonly ComplianceRecordStatus[] = [
  'verified',
  'expiring_soon',
  'expired',
] as const;

const PERSON_STATUS_LABELS: Record<PersonStatus, string> = {
  active: 'Active',
  onboarding: 'Onboarding',
  inactive: 'Inactive',
  stood_down: 'Stood down',
};

/** NSW OCG employer-verification portal. Hardcoded per PRD 06 §WWCC help
 *  panel — the per-organisation OCG org number lives in admin config
 *  (not yet surfaced to the admin SPA; a follow-up slice adds it). */
const OCG_PORTAL_URL =
  'https://www.kidsguardian.nsw.gov.au/child-safe-organisations/working-with-children-check/employer-verification';

/** The help-panel copy is version-controlled alongside the component.
 *  Bumping this date is the gesture that proves the compliance lead
 *  has re-checked the OCG portal flow and the copy still reflects
 *  current behaviour (PRD 06 §WWCC help panel). */
const WWCC_HELP_LAST_REVIEWED = '2026-04-24';

export function ComplianceRecordDetailPage({
  client,
  signInPath = '/admin/sign-in',
}: ComplianceRecordDetailPageProps): JSX.Element {
  const { recordId } = useParams<{ recordId: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });
  const [notes, setNotes] = useState('');
  const [expiryOverride, setExpiryOverride] = useState('');
  const [wwccOutcome, setWwccOutcome] = useState<WwccOutcome>('valid');
  const [prohibitionNoticeCompleted, setProhibitionNoticeCompleted] = useState(false);
  const [reverifyReason, setReverifyReason] =
    useState<RequestReverificationReason>('wwcc_not_found');
  const [reverifyNote, setReverifyNote] = useState('');
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(
    async (opts: { silent?: boolean } = {}): Promise<void> => {
      if (!recordId) {
        setState({ kind: 'not_found' });
        return;
      }
      // Silent refresh (e.g. after a successful upload-on-behalf) keeps the
      // current ready state in place so nested components like the
      // UploadOnBehalfPanel don't unmount and lose their success banner.
      if (!opts.silent) {
        setState({ kind: 'loading' });
      }
      try {
        const data = await client.getAdminComplianceRecordDetail(recordId);
        setState({ kind: 'ready', data });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          redirectToSignIn();
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'not_found' });
          return;
        }
        setState({
          kind: 'error',
          error:
            err instanceof ApiError
              ? err
              : "We couldn't load this record. Please check your connection and try again.",
        });
      }
    },
    [client, recordId, redirectToSignIn],
  );

  useEffect(() => {
    setSubmit({ kind: 'idle' });
  }, [recordId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onVerify = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      if (state.kind !== 'ready' || !recordId) return;
      const trimmedNotes = notes.trim();
      if (trimmedNotes.length === 0) {
        setSubmit({
          kind: 'error',
          code: 'notes_required',
          message: 'Please record a short verification note before confirming.',
          apiError: null,
        });
        return;
      }
      const isWwcc = state.data.requirement.short_code === 'WWCC';
      setSubmit({ kind: 'submitting' });
      try {
        const result = await client.verifyAdminComplianceRecord(recordId, {
          verification_notes: trimmedNotes,
          expiry_date_override: expiryOverride.trim().length > 0 ? expiryOverride.trim() : null,
          outcome: wwccOutcome,
          prohibition_notice_check_completed: isWwcc ? prohibitionNoticeCompleted : null,
        });
        setSubmit({ kind: 'success_verify', result });
        await load();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          redirectToSignIn();
          return;
        }
        if (err instanceof ApiError) {
          setSubmit({ kind: 'error', code: err.code, message: messageFor(err), apiError: err });
          return;
        }
        setSubmit({
          kind: 'error',
          code: 'network_error',
          message:
            "We couldn't submit the verification. Check your connection and try again — no changes were saved.",
          apiError: null,
        });
      }
    },
    [
      client,
      expiryOverride,
      load,
      notes,
      prohibitionNoticeCompleted,
      recordId,
      redirectToSignIn,
      state,
      wwccOutcome,
    ],
  );

  const onRequestReverification = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      if (state.kind !== 'ready' || !recordId) return;
      const trimmedNote = reverifyNote.trim();
      if (trimmedNote.length === 0) {
        setSubmit({
          kind: 'error',
          code: 'reverify_note_required',
          message: 'Please write a short note for the volunteer before requesting re-verification.',
          apiError: null,
        });
        return;
      }
      setSubmit({ kind: 'submitting' });
      try {
        await client.requestAdminReverification(recordId, {
          reason: reverifyReason,
          note: trimmedNote,
        });
        setSubmit({ kind: 'success_reverify_requested' });
        await load();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          redirectToSignIn();
          return;
        }
        if (err instanceof ApiError) {
          setSubmit({ kind: 'error', code: err.code, message: messageFor(err), apiError: err });
          return;
        }
        setSubmit({
          kind: 'error',
          code: 'network_error',
          message:
            "We couldn't record the re-verification request. Check your connection and try again — no changes were saved.",
          apiError: null,
        });
      }
    },
    [client, load, recordId, redirectToSignIn, reverifyNote, reverifyReason, state],
  );

  return (
    <main className="admin-record-detail" aria-labelledby="admin-record-detail-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin">← Back to dashboard</Link>
      </nav>

      {state.kind === 'loading' ? (
        <p className="admin-people-loading" aria-busy="true">
          Loading verification details…
        </p>
      ) : null}

      {state.kind === 'not_found' ? (
        <div className="admin-people-error" role="alert">
          <p>We couldn't find that compliance record. It may have been archived.</p>
          <Link to="/admin">Return to the admin dashboard</Link>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="admin-people-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <RecordBody
          client={client}
          data={state.data}
          notes={notes}
          expiryOverride={expiryOverride}
          wwccOutcome={wwccOutcome}
          prohibitionNoticeCompleted={prohibitionNoticeCompleted}
          reverifyReason={reverifyReason}
          reverifyNote={reverifyNote}
          submit={submit}
          onNotesChange={setNotes}
          onExpiryOverrideChange={setExpiryOverride}
          onWwccOutcomeChange={setWwccOutcome}
          onProhibitionNoticeChange={setProhibitionNoticeCompleted}
          onReverifyReasonChange={setReverifyReason}
          onReverifyNoteChange={setReverifyNote}
          onVerify={onVerify}
          onRequestReverification={onRequestReverification}
          onUploadComplete={() => void load({ silent: true })}
          onUnauthorised={redirectToSignIn}
        />
      ) : null}
    </main>
  );
}

interface RecordBodyProps {
  client: ApiClient;
  data: ComplianceRecordDetailResponse;
  notes: string;
  expiryOverride: string;
  wwccOutcome: WwccOutcome;
  prohibitionNoticeCompleted: boolean;
  reverifyReason: RequestReverificationReason;
  reverifyNote: string;
  submit: SubmitState;
  onNotesChange: (value: string) => void;
  onExpiryOverrideChange: (value: string) => void;
  onWwccOutcomeChange: (value: WwccOutcome) => void;
  onProhibitionNoticeChange: (value: boolean) => void;
  onReverifyReasonChange: (value: RequestReverificationReason) => void;
  onReverifyNoteChange: (value: string) => void;
  onVerify: (ev: React.FormEvent<HTMLFormElement>) => void;
  onRequestReverification: (ev: React.FormEvent<HTMLFormElement>) => void;
  onUploadComplete: () => void;
  onUnauthorised: () => void;
}

function RecordBody(props: RecordBodyProps): JSX.Element {
  const {
    client,
    data,
    notes,
    expiryOverride,
    wwccOutcome,
    prohibitionNoticeCompleted,
    reverifyReason,
    reverifyNote,
    submit,
    onNotesChange,
    onExpiryOverrideChange,
    onWwccOutcomeChange,
    onProhibitionNoticeChange,
    onReverifyReasonChange,
    onReverifyNoteChange,
    onVerify,
    onRequestReverification,
    onUploadComplete,
    onUnauthorised,
  } = props;
  const { record, person, requirement, evidence } = data;
  const isWwcc = requirement.short_code === 'WWCC';
  const isVerifiable = useMemo(() => VERIFIABLE_STATUSES.includes(record.status), [record.status]);
  const hasEvidenceAnchor =
    record.evidence_id !== null || record.acknowledgement_signature_id !== null;
  // Typed-name acknowledgement records are backed by an
  // `acknowledgement_signature_id` — there is no file to upload, so
  // the Upload-on-behalf panel makes no sense for them.
  const canUploadOnBehalf = isVerifiable && requirement.pattern !== 'sign';
  const canSupersede = useMemo(
    () => SUPERSEDABLE_STATUSES.includes(record.status),
    [record.status],
  );

  return (
    <>
      <header className="admin-record-header">
        <h1 id="admin-record-detail-heading">Verify: {requirement.name}</h1>
        <p className="admin-record-subtitle">
          <span>Record #{toIdString(record.record_id)}</span>
          <span aria-hidden="true"> · </span>
          <span className={`admin-record-status admin-record-status-${record.status}`}>
            {RECORD_STATUS_LABELS[record.status]}
          </span>
        </p>
      </header>

      <section className="admin-record-section" aria-labelledby="admin-record-person-heading">
        <h2 id="admin-record-person-heading">Volunteer on file</h2>
        <dl className="admin-record-dl">
          <dt>Full name</dt>
          <dd>
            <strong>{person.full_name}</strong>
            {person.preferred_name ? ` (${person.preferred_name})` : null}
          </dd>
          <dt>Email</dt>
          <dd>{person.contact_email}</dd>
          <dt>Date of birth</dt>
          <dd>{isoDateOnly(person.date_of_birth)}</dd>
          <dt>Status</dt>
          <dd>
            {PERSON_STATUS_LABELS[person.status]}
            {person.direct_child_contact ? ' · Direct child contact' : null}
          </dd>
        </dl>
        <p className="admin-record-hint">
          Confirm the name above matches exactly what appears on the uploaded evidence. If it
          doesn't, decline to verify and request a fresh upload from the volunteer.
        </p>
      </section>

      <section className="admin-record-section" aria-labelledby="admin-record-evidence-heading">
        <h2 id="admin-record-evidence-heading">Evidence</h2>
        {evidence ? (
          <EvidencePreview preview={evidence} />
        ) : record.acknowledgement_signature_id ? (
          <p className="admin-record-empty">
            This record is backed by a typed-name acknowledgement signature, not a file upload. The
            signed statement was captured automatically when the volunteer submitted the
            acknowledgement form.
          </p>
        ) : (
          <p className="admin-record-empty">
            No evidence has been uploaded yet. Ask the volunteer to sign in at <code>/sign-in</code>
            and upload their certificate, or upload on their behalf from this record.
          </p>
        )}
      </section>

      {isWwcc && isVerifiable ? <WwccHelpPanel person={person} /> : null}

      {canUploadOnBehalf ? (
        <UploadOnBehalfPanel
          client={client}
          recordId={record.record_id}
          personId={record.person_id}
          requirementId={record.requirement_id}
          hasExistingEvidence={evidence !== null}
          onUploadComplete={onUploadComplete}
          onUnauthorised={onUnauthorised}
        />
      ) : null}

      {canSupersede ? (
        <SupersedePanel
          client={client}
          recordId={record.record_id}
          candidates={data.supersede_candidates}
          onChange={onUploadComplete}
          onUnauthorised={onUnauthorised}
        />
      ) : null}

      <section className="admin-record-section" aria-labelledby="admin-record-verify-heading">
        <h2 id="admin-record-verify-heading">Verify</h2>
        {record.status === 'verified' ? (
          <VerifiedBlock record={record} />
        ) : !isVerifiable ? (
          <p className="admin-record-empty">
            This record isn't in a verifiable state (status: {RECORD_STATUS_LABELS[record.status]}).
            Verification is only available while a record is pending or awaiting verification.
          </p>
        ) : !hasEvidenceAnchor ? (
          <p className="admin-record-empty">
            Add evidence or wait for the volunteer to upload their certificate before verifying.
          </p>
        ) : isWwcc && wwccOutcome === 'not_found' ? (
          <RequestReverificationForm
            reason={reverifyReason}
            note={reverifyNote}
            submit={submit}
            onReasonChange={onReverifyReasonChange}
            onNoteChange={onReverifyNoteChange}
            onSubmit={onRequestReverification}
            onBackToOutcome={() => onWwccOutcomeChange('valid')}
          />
        ) : (
          <VerifyForm
            isWwcc={isWwcc}
            notes={notes}
            expiryOverride={expiryOverride}
            wwccOutcome={wwccOutcome}
            prohibitionNoticeCompleted={prohibitionNoticeCompleted}
            submit={submit}
            onNotesChange={onNotesChange}
            onExpiryOverrideChange={onExpiryOverrideChange}
            onWwccOutcomeChange={onWwccOutcomeChange}
            onProhibitionNoticeChange={onProhibitionNoticeChange}
            onSubmit={onVerify}
          />
        )}

        {submit.kind === 'success_verify' ? (
          <p className="admin-record-verify-success" role="status">
            {submit.result.person_status === 'stood_down'
              ? 'Flagged as blocked. The volunteer has been stood down and the compliance lead has been notified.'
              : 'Verified. A "you\'re compliant" email has been queued and the 14-day NQA ITS sync task has been raised.'}
          </p>
        ) : null}
        {submit.kind === 'success_reverify_requested' ? (
          <p className="admin-record-verify-success" role="status">
            Re-verification requested. The record stays awaiting verification; a follow-up email
            asking the volunteer to reconfirm their details will go out shortly.
          </p>
        ) : null}
      </section>
    </>
  );
}

interface VerifiedBlockProps {
  record: ComplianceRecordDetailResponse['record'];
}

function VerifiedBlock({ record }: VerifiedBlockProps): JSX.Element {
  return (
    <div className="admin-record-verified-block">
      <p>
        Verified on <strong>{isoDateOnly(record.verified_on)}</strong>
        {record.expiry_date ? (
          <>
            {' '}
            · expires <strong>{isoDateOnly(record.expiry_date)}</strong>
          </>
        ) : null}
      </p>
      {record.verification_notes ? (
        <p className="admin-record-verified-notes">"{record.verification_notes}"</p>
      ) : null}
    </div>
  );
}

interface VerifyFormProps {
  isWwcc: boolean;
  notes: string;
  expiryOverride: string;
  wwccOutcome: WwccOutcome;
  prohibitionNoticeCompleted: boolean;
  submit: SubmitState;
  onNotesChange: (value: string) => void;
  onExpiryOverrideChange: (value: string) => void;
  onWwccOutcomeChange: (value: WwccOutcome) => void;
  onProhibitionNoticeChange: (value: boolean) => void;
  onSubmit: (ev: React.FormEvent<HTMLFormElement>) => void;
}

function VerifyForm(props: VerifyFormProps): JSX.Element {
  const {
    isWwcc,
    notes,
    expiryOverride,
    wwccOutcome,
    prohibitionNoticeCompleted,
    submit,
    onNotesChange,
    onExpiryOverrideChange,
    onWwccOutcomeChange,
    onProhibitionNoticeChange,
    onSubmit,
  } = props;
  const submitting = submit.kind === 'submitting';
  const submitLabel = submitting
    ? isWwcc && (wwccOutcome === 'barred' || wwccOutcome === 'interim_bar')
      ? 'Flagging…'
      : 'Verifying…'
    : isWwcc && (wwccOutcome === 'barred' || wwccOutcome === 'interim_bar')
      ? 'Flag as blocked'
      : 'Confirm and verify';

  return (
    <form className="admin-record-verify-form" onSubmit={onSubmit} noValidate>
      {isWwcc ? (
        <fieldset className="admin-record-fieldset">
          <legend className="admin-record-label">
            Portal outcome
            <span className="admin-record-required-marker" aria-hidden="true">
              {' '}
              *
            </span>
            <span className="sr-only"> (required)</span>
          </legend>
          {WWCC_OUTCOME_OPTIONS.map((option) => (
            <label key={option.value} className="admin-record-radio-row">
              <input
                type="radio"
                name="wwcc-outcome"
                value={option.value}
                checked={wwccOutcome === option.value}
                onChange={() => onWwccOutcomeChange(option.value)}
                disabled={submitting}
              />
              <span>
                <strong>{option.label}</strong>
                <span className="admin-record-radio-hint"> — {option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <label htmlFor="admin-verify-notes" className="admin-record-label">
        Verification notes
        <span className="admin-record-required-marker" aria-hidden="true">
          {' '}
          *
        </span>
        <span className="sr-only"> (required)</span>
      </label>
      <textarea
        id="admin-verify-notes"
        className="admin-record-textarea"
        value={notes}
        onChange={(ev) => onNotesChange(ev.target.value)}
        required
        rows={4}
        placeholder="Name on the certificate matches the record; completion date 2026-04-15."
        disabled={submitting}
      />

      <label htmlFor="admin-verify-expiry" className="admin-record-label">
        {isWwcc ? 'Clearance expiry date' : 'Expiry date (optional override)'}
      </label>
      <input
        id="admin-verify-expiry"
        className="admin-record-input"
        type="date"
        value={expiryOverride}
        onChange={(ev) => onExpiryOverrideChange(ev.target.value)}
        disabled={submitting}
      />
      <p className="admin-record-hint">
        {isWwcc
          ? 'Type the clearance expiry date from the portal result — five years from the WWCC issue date for a Cleared outcome.'
          : 'Leave blank to compute the expiry from the completion date on file. Use the override when the evidence carries its own expiry.'}
      </p>

      {isWwcc ? (
        <label className="admin-record-checkbox-row">
          <input
            type="checkbox"
            checked={prohibitionNoticeCompleted}
            onChange={(ev) => onProhibitionNoticeChange(ev.target.checked)}
            disabled={submitting}
          />
          <span>
            <strong>Prohibition notice check completed</strong>
            <span className="admin-record-radio-hint">
              {' '}
              — confirm you ran the NQA ITS prohibition-notice check for this volunteer (day-one
              rollout fold-in).
            </span>
          </span>
        </label>
      ) : null}

      {submit.kind === 'error' ? (
        <ErrorBanner error={submit.apiError ?? submit.message} message={submit.message} />
      ) : null}

      <button type="submit" className="admin-record-verify-button" disabled={submitting}>
        {submitLabel}
      </button>
    </form>
  );
}

interface RequestReverificationFormProps {
  reason: RequestReverificationReason;
  note: string;
  submit: SubmitState;
  onReasonChange: (value: RequestReverificationReason) => void;
  onNoteChange: (value: string) => void;
  onSubmit: (ev: React.FormEvent<HTMLFormElement>) => void;
  onBackToOutcome: () => void;
}

function RequestReverificationForm(props: RequestReverificationFormProps): JSX.Element {
  const { reason, note, submit, onReasonChange, onNoteChange, onSubmit, onBackToOutcome } = props;
  const submitting = submit.kind === 'submitting';

  return (
    <form className="admin-record-verify-form" onSubmit={onSubmit} noValidate>
      <p className="admin-record-hint">
        Requesting re-verification keeps this record <strong>awaiting verification</strong>. The
        volunteer will be asked to reconfirm their details and re-upload the portal result. Nothing
        on the record is marked verified.
      </p>

      <label htmlFor="admin-reverify-reason" className="admin-record-label">
        Reason for re-verification
      </label>
      <select
        id="admin-reverify-reason"
        className="admin-record-input"
        value={reason}
        onChange={(ev) => onReasonChange(ev.target.value as RequestReverificationReason)}
        disabled={submitting}
      >
        {REVERIFY_REASON_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <label htmlFor="admin-reverify-note" className="admin-record-label">
        Note to the volunteer
        <span className="admin-record-required-marker" aria-hidden="true">
          {' '}
          *
        </span>
        <span className="sr-only"> (required)</span>
      </label>
      <textarea
        id="admin-reverify-note"
        className="admin-record-textarea"
        value={note}
        onChange={(ev) => onNoteChange(ev.target.value)}
        required
        rows={4}
        placeholder={
          'The OCG portal could not find this WWCC number. Please reconfirm your clearance number and DOB, then upload a fresh screenshot via /sign-in.'
        }
        disabled={submitting}
      />

      {submit.kind === 'error' ? (
        <ErrorBanner error={submit.apiError ?? submit.message} message={submit.message} />
      ) : null}

      <div className="admin-record-verify-actions">
        <button
          type="button"
          className="admin-record-secondary-button"
          onClick={onBackToOutcome}
          disabled={submitting}
        >
          Back to outcome
        </button>
        <button type="submit" className="admin-record-verify-button" disabled={submitting}>
          {submitting ? 'Requesting…' : 'Request re-verification'}
        </button>
      </div>
    </form>
  );
}

interface WwccHelpPanelProps {
  person: ComplianceRecordDetailResponse['person'];
}

function WwccHelpPanel({ person }: WwccHelpPanelProps): JSX.Element {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback(async (field: string, value: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      }
      setCopied(field);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard API may be blocked in non-HTTPS contexts or by
      // browser policy. Fall back to a non-toast no-op — the value
      // is still on-screen and the admin can triple-click to select.
      setCopied(null);
    }
  }, []);

  return (
    <aside
      className="admin-record-help admin-record-help-wwcc"
      aria-labelledby="admin-record-help-heading"
    >
      <details open>
        <summary>
          <h2 id="admin-record-help-heading" className="admin-record-help-heading">
            How to verify this WWCC
          </h2>
        </summary>
        <p className="admin-record-help-reviewed">
          Last reviewed: <time dateTime={WWCC_HELP_LAST_REVIEWED}>{WWCC_HELP_LAST_REVIEWED}</time>
        </p>
        <p className="admin-record-help-intro">
          The NSW Office of the Children's Guardian has no API, so every WWCC check is a portal
          round-trip. Follow the numbered steps — target time is under 3 minutes once you are signed
          into the OCG portal.
        </p>

        <ol className="admin-record-help-steps">
          <li>
            <strong>Open the OCG portal.</strong> Use the button below — sign in the first time;
            subsequent verifications in the same session reuse your portal login.
            <p>
              <a
                className="admin-record-help-portal-link"
                href={OCG_PORTAL_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open OCG employer-verification portal
              </a>
            </p>
          </li>
          <li>
            <strong>Copy the volunteer's details.</strong> Click any tile below to copy the value to
            your clipboard.
            <div className="admin-record-help-copy-grid">
              <CopyTile
                field="full_name"
                label="Full name"
                value={person.full_name}
                copied={copied === 'full_name'}
                onCopy={copy}
              />
              <CopyTile
                field="dob"
                label="Date of birth"
                value={isoDateOnly(person.date_of_birth)}
                copied={copied === 'dob'}
                onCopy={copy}
              />
            </div>
          </li>
          <li>
            <strong>Run the portal check.</strong> The portal returns one of: <em>Cleared</em>{' '}
            (good), <em>Barred</em> / <em>Interim Bar</em> (stop — escalate), or <em>Not Found</em>{' '}
            (number wrong, application still in progress, or name/DOB don't match).
          </li>
          <li>
            <strong>Capture the result.</strong> Use the portal's <em>Print to PDF</em> option or
            your OS screenshot tool. The capture must show the clearance number, the volunteer's
            name, the verification outcome, the clearance expiry date, and today's date.
          </li>
          <li>
            <strong>Return to this screen,</strong> select the <em>Portal outcome</em> radio below,
            type the clearance expiry date, add a short note, and click <em>Verify</em>. For a
            non-Cleared outcome see the unusual-outcome guidance below.
          </li>
        </ol>

        <details className="admin-record-help-unusual">
          <summary>What to do on an unusual outcome</summary>
          <ul>
            <li>
              <strong>Barred / Interim Bar.</strong> Do not verify. Select the outcome and click{' '}
              <em>Flag as blocked</em>. The server writes the outcome onto the record, sets the
              volunteer's status to <code>stood_down</code>, and sends an escalation email to the
              compliance lead. The volunteer is not told the outcome via this flow — that
              conversation is handled out-of-band.
            </li>
            <li>
              <strong>Not Found.</strong> Do not verify. Select <em>Not Found</em>, then use the
              <em> Request re-verification</em> form that appears and ask the volunteer to confirm
              their clearance number and DOB.
            </li>
            <li>
              <strong>Name or DOB mismatch.</strong> Do not verify. Select <em>Not Found</em> and
              choose the matching reason from the re-verification form (name mismatch / DOB
              mismatch), then write a polite note explaining the mismatch.
            </li>
            <li>
              <strong>Portal down or slow.</strong> Pause and come back later. The record stays on
              the "Awaiting verification" list until resolved; nothing is ever auto-verified.
            </li>
          </ul>
        </details>

        <p className="admin-record-help-tip" role="note">
          <strong>Tip.</strong> Stay signed into the OCG portal for the day and verify in batches —
          the dashboard's <em>Awaiting verification</em> filter lines up every pending WWCC.
        </p>
      </details>
    </aside>
  );
}

interface CopyTileProps {
  field: string;
  label: string;
  value: string | null;
  copied: boolean;
  onCopy: (field: string, value: string) => Promise<void>;
}

function CopyTile({ field, label, value, copied, onCopy }: CopyTileProps): JSX.Element {
  const disabled = value === null;
  return (
    <button
      type="button"
      className="admin-record-help-copy-tile"
      onClick={disabled ? undefined : () => void onCopy(field, value!)}
      disabled={disabled}
      aria-label={`Copy ${label}: ${value ?? 'unavailable'}`}
    >
      <span className="admin-record-help-copy-label">{label}</span>
      <span className="admin-record-help-copy-value">{value ?? '—'}</span>
      <span className="admin-record-help-copy-status" aria-live="polite">
        {copied ? 'Copied!' : disabled ? 'Unavailable' : 'Click to copy'}
      </span>
    </button>
  );
}

interface EvidencePreviewProps {
  preview: NonNullable<ComplianceRecordDetailResponse['evidence']>;
}

function EvidencePreview({ preview }: EvidencePreviewProps): JSX.Element {
  const { evidence, download_url } = preview;
  const isPdf = evidence.mime_type === 'application/pdf';
  const isImage = evidence.mime_type === 'image/png' || evidence.mime_type === 'image/jpeg';
  const sizeKb = Math.round(Number(evidence.size_bytes) / 1024);

  return (
    <div className="admin-record-evidence">
      <dl className="admin-record-dl">
        <dt>File</dt>
        <dd>
          <strong>{evidence.original_filename}</strong>
          <span className="admin-record-evidence-meta">
            {' '}
            ({evidence.mime_type}, {sizeKb} kB)
          </span>
        </dd>
        <dt>Uploaded</dt>
        <dd>
          {isoDateOnly(evidence.uploaded_at)} by {evidence.uploaded_by_kind}
        </dd>
      </dl>
      {isPdf ? (
        <iframe
          className="admin-record-evidence-frame"
          src={download_url}
          title={`Evidence preview: ${evidence.original_filename}`}
        />
      ) : isImage ? (
        <img
          className="admin-record-evidence-image"
          src={download_url}
          alt={`Evidence preview: ${evidence.original_filename}`}
        />
      ) : null}
      <p className="admin-record-evidence-fallback">
        <a href={download_url} target="_blank" rel="noreferrer noopener">
          Open evidence in a new tab
        </a>
      </p>
    </div>
  );
}

/**
 * UploadOnBehalfPanel — admin "Upload on behalf" affordance (PRD 06
 * §Edge cases, M13). Renders above the verify form whenever the
 * record is pending / submitted and the item type expects a file
 * (not typed_name_acknowledgement). Runs the three-step chain on
 * submit: `POST /uploads/request-url` → direct PUT to the presigned
 * URL → `POST /uploads/confirm` → `POST /compliance-records/:recordId/
 * attach-evidence`. The required note captures *why* admin is
 * uploading (forwarded to shared mailbox, volunteer offline,
 * wet-signature scan, etc.) and lands in `verification_notes` so the
 * next admin sees the upload context.
 */
interface UploadOnBehalfPanelProps {
  client: ApiClient;
  recordId: string | number | bigint;
  personId: string | number | bigint;
  requirementId: string | number | bigint;
  hasExistingEvidence: boolean;
  onUploadComplete: () => void;
  onUnauthorised: () => void;
}

type UploadOnBehalfState =
  | { kind: 'idle' }
  | { kind: 'uploading'; phase: 'requesting_url' | 'putting' | 'confirming' | 'attaching' }
  | { kind: 'success' }
  | { kind: 'error'; message: string; apiError: ApiError | null };

function UploadOnBehalfPanel(props: UploadOnBehalfPanelProps): JSX.Element {
  const {
    client,
    recordId,
    personId,
    requirementId,
    hasExistingEvidence,
    onUploadComplete,
    onUnauthorised,
  } = props;
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<UploadOnBehalfState>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploading = state.kind === 'uploading';

  const onSubmit = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      const trimmedNote = note.trim();
      if (!file) {
        setState({
          kind: 'error',
          message: 'Please choose a file to upload before submitting.',
          apiError: null,
        });
        return;
      }
      if (trimmedNote.length === 0) {
        setState({
          kind: 'error',
          message: 'Please add a short note explaining why you are uploading on behalf.',
          apiError: null,
        });
        return;
      }

      try {
        setState({ kind: 'uploading', phase: 'requesting_url' });
        const presigned = await client.requestAdminEvidenceUploadUrl({
          person_id: personId,
          requirement_id: requirementId,
          filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
        });

        setState({ kind: 'uploading', phase: 'putting' });
        await client.putEvidenceFile(presigned.upload_url, file);

        setState({ kind: 'uploading', phase: 'confirming' });
        const confirmed = await client.confirmAdminEvidenceUpload({
          upload_session_id: presigned.upload_session_id,
          storage_key: presigned.storage_key,
          on_behalf_note: trimmedNote,
        });

        setState({ kind: 'uploading', phase: 'attaching' });
        await client.attachAdminEvidenceToRecord(recordId as string, {
          evidence_id: confirmed.evidence.evidence_id,
          on_behalf_note: trimmedNote,
        });

        setState({ kind: 'success' });
        setFile(null);
        setNote('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        onUploadComplete();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorised();
          return;
        }
        setState({
          kind: 'error',
          message:
            err instanceof ApiError
              ? messageForUpload(err)
              : err instanceof Error
                ? err.message
                : 'Upload failed. Check your connection and try again — no changes were saved.',
          apiError: err instanceof ApiError ? err : null,
        });
      }
    },
    [client, file, requirementId, note, onUnauthorised, onUploadComplete, personId, recordId],
  );

  return (
    <section
      className="admin-record-section admin-record-upload-on-behalf"
      aria-labelledby="admin-record-upload-heading"
    >
      <h2 id="admin-record-upload-heading">
        {hasExistingEvidence ? 'Replace evidence on behalf' : 'Upload on behalf'}
      </h2>
      <p className="admin-record-hint">
        Use this when the volunteer forwarded their certificate by email, is unable to use the
        self-service portal, or you captured a portal-result PDF yourself. The file is attached to
        this record and the note is preserved in the audit trail.
      </p>
      <form
        className="admin-record-upload-form"
        onSubmit={onSubmit}
        noValidate
        aria-describedby="admin-record-upload-hint"
      >
        <label htmlFor="admin-record-upload-file" className="admin-record-label">
          File
          <span className="admin-record-required-marker" aria-hidden="true">
            {' '}
            *
          </span>
          <span className="sr-only"> (required)</span>
        </label>
        <input
          id="admin-record-upload-file"
          ref={fileInputRef}
          className="admin-record-input"
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={uploading}
          required
        />
        <p id="admin-record-upload-hint" className="admin-record-hint">
          PDF, PNG, or JPEG — up to 20 MB.
        </p>

        <label htmlFor="admin-record-upload-note" className="admin-record-label">
          Why are you uploading on their behalf?
          <span className="admin-record-required-marker" aria-hidden="true">
            {' '}
            *
          </span>
          <span className="sr-only"> (required)</span>
        </label>
        <textarea
          id="admin-record-upload-note"
          className="admin-record-textarea"
          value={note}
          onChange={(ev) => setNote(ev.target.value)}
          rows={3}
          placeholder="Volunteer forwarded the certificate by email on 2026-04-24."
          disabled={uploading}
          required
        />

        {state.kind === 'error' ? (
          <ErrorBanner error={state.apiError ?? state.message} message={state.message} />
        ) : null}
        {state.kind === 'success' ? (
          <p className="admin-record-verify-success" role="status">
            Evidence attached. The record is now awaiting verification — complete the verify form
            below to finish.
          </p>
        ) : null}
        {uploading ? (
          <p className="admin-record-upload-progress" role="status" aria-live="polite">
            {uploadPhaseLabel(state.phase)}
          </p>
        ) : null}

        <button type="submit" className="admin-record-verify-button" disabled={uploading}>
          {uploading
            ? 'Uploading…'
            : hasExistingEvidence
              ? 'Replace evidence'
              : 'Upload and attach'}
        </button>
      </form>
    </section>
  );
}

function uploadPhaseLabel(
  phase: 'requesting_url' | 'putting' | 'confirming' | 'attaching',
): string {
  switch (phase) {
    case 'requesting_url':
      return 'Preparing upload…';
    case 'putting':
      return 'Uploading file…';
    case 'confirming':
      return 'Saving evidence…';
    case 'attaching':
      return 'Attaching to record…';
  }
}

function messageForUpload(err: ApiError): string {
  switch (err.code) {
    case 'invalid_status_transition':
      return 'This record is no longer accepting uploads — it may have been verified already. Reload to see the current state.';
    case 'invalid_mime_type':
      return 'That file type is not allowed. Upload a PDF, PNG, or JPEG.';
    case 'file_too_large':
      return 'That file is too large. The 20 MB limit applies to admin uploads too.';
    case 'evidence_not_found':
      return 'The server could not match the evidence to this record. Reload and try again.';
    case 'upload_session_not_found':
    case 'upload_session_mismatch':
      return 'Your upload session is out of sync. Reload the page and upload the file again.';
    case 'record_not_found':
      return 'This record no longer exists. It may have been archived.';
    default:
      return err.message;
  }
}

function toIdString(id: ComplianceRecordDetailResponse['record']['record_id']): string {
  return typeof id === 'string' ? id : String(id);
}

function isoDateOnly(v: string | Date | null): string {
  if (!v) return '—';
  const s = typeof v === 'string' ? v : v.toISOString();
  return s.slice(0, 10);
}

function messageFor(err: ApiError): string {
  switch (err.code) {
    case 'invalid_status_transition':
      return 'This record is not in a verifiable state anymore. Reload to see the current state.';
    case 'evidence_required':
      return 'Add evidence or wait for the volunteer to upload their certificate before verifying.';
    case 'record_not_found':
      return 'This record no longer exists. It may have been archived.';
    case 'validation_failed':
      return 'Please check the verification notes and expiry date, then try again.';
    default:
      return err.message;
  }
}

/**
 * SupersedePanel — admin supersede affordance (PRD 03 §3, M13 —
 * "supersede affordance inline on the per-record list"). Renders above
 * the Verify section when the record is in a supersedable status
 * (`verified`/`expiring_soon`/`expired`). The picker is pre-populated
 * from `supersede_candidates` (same person + item type, non-terminal,
 * excluding the current record) so the admin cannot click a row that
 * would 400. Empty-candidate case shows the hint "create a new record
 * first" — typically by verifying the renewal, which then surfaces
 * here on the old record.
 *
 * Mark-as-superseded is a one-way action: the outgoing record
 * transitions to `superseded`, no daily reminders fire on it, and the
 * dashboard "Expired and overdue" filter no longer surfaces it.
 */
interface SupersedePanelProps {
  client: ApiClient;
  recordId: string | number | bigint;
  candidates: ComplianceRecordDetailResponse['supersede_candidates'];
  onChange: () => void;
  onUnauthorised: () => void;
}

type SupersedePanelMode = { kind: 'collapsed' } | { kind: 'picking' };

type SupersedeSubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string; apiError: ApiError | null };

function SupersedePanel(props: SupersedePanelProps): JSX.Element {
  const { client, recordId, candidates, onChange, onUnauthorised } = props;
  const [mode, setMode] = useState<SupersedePanelMode>({ kind: 'collapsed' });
  const [selectedId, setSelectedId] = useState<string>('');
  const [submit, setSubmit] = useState<SupersedeSubmitState>({ kind: 'idle' });

  const openPicker = useCallback(() => {
    setSelectedId(candidates[0] ? String(candidates[0].record_id) : '');
    setSubmit({ kind: 'idle' });
    setMode({ kind: 'picking' });
  }, [candidates]);

  const cancel = useCallback(() => {
    setMode({ kind: 'collapsed' });
    setSubmit({ kind: 'idle' });
  }, []);

  const onSubmit = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      if (selectedId.trim().length === 0) {
        setSubmit({
          kind: 'error',
          message: 'Please pick the record that supersedes this one.',
          apiError: null,
        });
        return;
      }
      setSubmit({ kind: 'submitting' });
      try {
        await client.supersedeAdminComplianceRecord(recordId as string, {
          superseded_by_id: selectedId,
        });
        setSubmit({ kind: 'success' });
        setMode({ kind: 'collapsed' });
        onChange();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorised();
          return;
        }
        setSubmit({
          kind: 'error',
          message:
            err instanceof ApiError
              ? supersedeErrorCopy(err)
              : "We couldn't supersede this record. Check your connection and try again — no changes were saved.",
          apiError: err instanceof ApiError ? err : null,
        });
      }
    },
    [client, onChange, onUnauthorised, recordId, selectedId],
  );

  const isBusy = submit.kind === 'submitting';
  const hasCandidates = candidates.length > 0;

  return (
    <section
      className="admin-record-section admin-record-supersede"
      aria-labelledby="admin-record-supersede-heading"
    >
      <h2 id="admin-record-supersede-heading">Supersede this record</h2>

      {hasCandidates ? (
        <p className="admin-record-hint">
          Mark this record as replaced by a newer one for the same item type. Reminders stop and the
          superseded record drops off the dashboard. Pick the replacement below.
        </p>
      ) : (
        <p className="admin-record-hint" data-testid="admin-record-supersede-no-candidates">
          No replacement record exists yet. Create (or verify) a new record for this item type for
          this volunteer, then return here to mark this one as superseded.
        </p>
      )}

      {mode.kind === 'collapsed' ? (
        <div className="admin-record-supersede-actions">
          <button
            type="button"
            className="admin-record-verify-button"
            onClick={openPicker}
            disabled={!hasCandidates}
            data-testid="admin-record-supersede-open"
          >
            Supersede…
          </button>
        </div>
      ) : null}

      {mode.kind === 'picking' ? (
        <form
          className="admin-record-supersede-form"
          onSubmit={onSubmit}
          data-testid="admin-record-supersede-form"
        >
          <label className="admin-record-form-row">
            <span className="admin-record-form-label">Replacement record</span>
            <select
              className="admin-record-form-input"
              value={selectedId}
              onChange={(ev) => setSelectedId(ev.target.value)}
              disabled={isBusy || !hasCandidates}
              data-testid="admin-record-supersede-select"
            >
              {candidates.map((c) => (
                <option key={String(c.record_id)} value={String(c.record_id)}>
                  Record #{String(c.record_id)} · {RECORD_STATUS_LABELS[c.status]}
                  {c.completed_on ? ` · completed ${isoDateOnly(c.completed_on)}` : ''}
                  {c.expiry_date ? ` · expires ${isoDateOnly(c.expiry_date)}` : ''}
                </option>
              ))}
            </select>
          </label>

          {submit.kind === 'error' ? (
            <ErrorBanner error={submit.apiError ?? submit.message} message={submit.message} />
          ) : null}

          <div className="admin-record-form-actions">
            <button
              type="submit"
              className="admin-record-verify-button"
              disabled={isBusy || !hasCandidates}
              data-testid="admin-record-supersede-confirm"
            >
              {isBusy ? 'Superseding…' : 'Confirm supersede'}
            </button>
            <button
              type="button"
              className="admin-record-cancel-button"
              onClick={cancel}
              disabled={isBusy}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {submit.kind === 'success' && mode.kind === 'collapsed' ? (
        <p className="admin-record-verify-success" role="status">
          Record superseded. The newer record remains active; this one drops off the reminder queue.
        </p>
      ) : null}
    </section>
  );
}

function supersedeErrorCopy(err: ApiError): string {
  switch (err.code) {
    case 'invalid_status_transition':
      return 'This record is no longer in a state that can be superseded. Reload to see the current state.';
    case 'self_supersede':
      return 'A record cannot supersede itself. Pick a different replacement.';
    case 'replacement_record_not_found':
      return 'That replacement record no longer exists. Reload to refresh the list.';
    case 'replacement_cross_person':
      return 'The replacement record belongs to a different volunteer. Reload and pick a valid replacement.';
    case 'replacement_cross_item_type':
      return 'The replacement record is for a different item type. Pick a replacement with the same item type.';
    case 'replacement_terminal':
      return 'The replacement record is already closed. Pick another active replacement.';
    case 'record_not_found':
      return 'This record no longer exists. It may have been archived.';
    case 'validation_failed':
      return 'Please pick a replacement record, then try again.';
    default:
      return err.message;
  }
}
